import { useState } from 'react'
import { api } from '../lib/api.js'
import { useConfirm } from './Ui.jsx'
import { addDays, fmtDate, todayISO } from '../lib/format.js'

const BLANK = {
  name: '', phone: '', village: '', mandal: '', district: '',
  quantityLakh: '', plStage: '', targetStockingDate: '', pondSalinityPpt: '', notes: ''
}

const DISTRICTS = [
  'Kakinada', 'Konaseema', 'East Godavari', 'West Godavari', 'Eluru',
  'Krishna', 'Bapatla', 'Guntur', 'Prakasam', 'Nellore', 'Other'
]

/**
 * The farmer-facing lead form. It captures enough for the desk to ring back with
 * something useful in hand — quantity, stocking date, pond location — and
 * nothing more. It books nothing; the operator still places every order.
 */
export default function EnquiryForm ({ hatchery, batch, onClose }) {
  const [form, setForm] = useState({
    ...BLANK,
    plStage: batch?.plStage || '',
    village: '',
    district: ''
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(null)
  const confirm = useConfirm()

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const submit = async e => {
    e.preventDefault()
    // The number is the whole lead — a wrong digit and the desk never reaches them.
    const ok = await confirm({
      title: 'Send this request to the desk?',
      body: 'The seed desk will ring you back on this number. Nothing is booked and no money is due yet.',
      facts: [
        ['Name', form.name],
        ['We will call', form.phone],
        hatchery && ['About', hatchery.name],
        form.quantityLakh && ['Quantity', `${form.quantityLakh} lakh PL`]
      ],
      confirmLabel: 'Send request'
    })
    if (!ok) return
    setBusy(true)
    setError(null)
    try {
      const res = await api.post('/public/enquiries', {
        ...form,
        hatcheryId: hatchery?.id || hatchery?._id || null,
        batchId: batch?.id || batch?._id || null
      })
      setDone(res.enquiry)
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <div className="enq-done">
        <div className="tick-big">✓</div>
        <h3>Request received</h3>
        <p>
          Thank you, {done.name}. The seed desk will call you on <b>{form.phone}</b>
          {done.hatcheryName ? <> about seed from <b>{done.hatcheryName}</b></> : null}.
        </p>
        <div className="enq-ref">
          <span>Your reference</span>
          <code>{done.enquiryCode}</code>
        </div>
        <p className="muted small">
          Quote this number when you call. Nothing is booked yet — the desk confirms
          availability and price with you first.
        </p>
        <button className="btn primary block" onClick={onClose}>Done</button>
      </div>
    )
  }

  return (
    <form className="enq-form" onSubmit={submit}>
      {(hatchery || batch) && (
        <div className="enq-context">
          <div className="k">You are asking about</div>
          <div className="v">
            {hatchery?.name}
            {batch && <> · {batch.plStage} ready {fmtDate(batch.readyDate)}</>}
          </div>
        </div>
      )}

      {error && <div className="alert error">{error}</div>}

      <div className="formgrid">
        <label className="f">
          <span>Your name<span className="req"> *</span></span>
          <input type="text" value={form.name} onChange={e => set('name', e.target.value)} required
            autoComplete="name" enterKeyHint="next" />
        </label>
        <label className="f">
          <span>Phone number<span className="req"> *</span></span>
          <input
            type="tel"
            inputMode="tel"
            value={form.phone}
            onChange={e => set('phone', e.target.value)}
            placeholder="10-digit mobile"
            autoComplete="tel"
            enterKeyHint="next"
            required
          />
        </label>
        <label className="f">
          <span>Village</span>
          <input type="text" value={form.village} onChange={e => set('village', e.target.value)}
            autoComplete="address-level3" enterKeyHint="next" />
        </label>
        <label className="f">
          <span>Mandal</span>
          <input type="text" value={form.mandal} onChange={e => set('mandal', e.target.value)} />
        </label>
        <label className="f">
          <span>District</span>
          <select value={form.district} onChange={e => set('district', e.target.value)}>
            <option value="">Choose…</option>
            {DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <label className="f">
          <span>Seed needed (lakh PL)</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.5"
            min="0"
            value={form.quantityLakh}
            onChange={e => set('quantityLakh', e.target.value)}
            placeholder="e.g. 10"
          />
        </label>
        <label className="f">
          <span>When will your pond be ready?</span>
          <input
            type="date"
            min={todayISO()}
            max={addDays(todayISO(), 120)}
            value={form.targetStockingDate}
            onChange={e => set('targetStockingDate', e.target.value)}
          />
        </label>
        <label className="f">
          <span>Pond salinity (ppt)</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.5"
            value={form.pondSalinityPpt}
            onChange={e => set('pondSalinityPpt', e.target.value)}
            placeholder="if you know it"
          />
        </label>
      </div>

      <label className="f">
        <span>Anything else the desk should know</span>
        <textarea
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
          placeholder="PL stage you prefer, pond condition, past hatcheries you have used…"
        />
      </label>

      <button className="btn primary block big" disabled={busy} type="submit">
        {busy ? 'Sending…' : 'Request a call back'}
      </button>
      <p className="muted small" style={{ marginTop: 10, marginBottom: 0 }}>
        This is a request, not a booking. The desk will call you to confirm availability, price and
        the sample test before anything is held.
      </p>
    </form>
  )
}
